import { EnvelopeIcon } from '@heroicons/react/24/outline'
import PropTypes from 'prop-types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import trackEvents from '../../../constants/track-events'
import { useNotificationContext } from '../../../context'
import { query } from '../../../graphql'
import { trackEvent } from '../../../helpers/analytics'
import { usePartnerIntegrationDelete } from '../../../hooks/use-partner-integration-delete'
import { usePartnerIntegrations } from '../../../hooks/use-partner-integrations'
import Button from '../../tailwind/Button'
import Modal from '../../tailwind/Modal'

const ModalDeleteUser = ({ open, onClose, onSuccess, data }) => {
  const { t } = useTranslation()
  const { success } = useNotificationContext()
  const [isDeleting, setIsDeleting] = useState(false)

  const { email, space_id } = data

  const { data: affectedIntegrations } = usePartnerIntegrations({
    select: (integrations) =>
      integrations.filter(
        (i) =>
          i.remote_service === 'EMAIL' &&
          i.integration_settings?.email === email
      )
  })

  const deleteIntegration = usePartnerIntegrationDelete()

  const handleDelete = async () => {
    setIsDeleting(true)

    if (affectedIntegrations?.length > 0) {
      await Promise.all(
        affectedIntegrations.map((i) =>
          deleteIntegration.mutateAsync({ input: { id: i.id } })
        )
      )
    }

    await query({
      query: 'userControl',
      variables: { action: 'deleteSpaceForPartner', space_id }
    })

    setIsDeleting(false)
    success(t('settings.user_management.delete_alert.success'))
    trackEvent(trackEvents.DELETE_USER)
    onSuccess()
  }

  return (
    <Modal open={open} setOpen={onClose} size='lg'>
      <span className='text-2xl font-bold tracking-tight text-gray-900'>
        {t('settings.user_management.delete_alert.title')}
      </span>
      <p className='mt-2 text-sm text-gray-500'>
        {t('settings.user_management.delete_alert.text', { email })}
      </p>
      {affectedIntegrations?.length > 0 && (
        <div className='mt-5 rounded-lg border border-gray-200 bg-gray-50'>
          <div className='border-b border-gray-200 px-4 py-2.5'>
            <p className='text-xs font-medium uppercase tracking-wide text-gray-500'>
              {t('settings.user_management.delete_alert.also_removed_title')}
            </p>
          </div>
          <ul className='divide-y divide-gray-100'>
            {affectedIntegrations.map((i) => (
              <li key={i.id} className='flex items-center gap-3 px-4 py-3'>
                <EnvelopeIcon className='h-4 w-4 flex-shrink-0 text-gray-400' />
                <span className='text-sm text-gray-700'>
                  {t('settings.user_management.delete_alert.email_integration_label')}
                </span>
                <span className='ml-auto truncate text-sm font-medium text-gray-900'>
                  {i.integration_settings.email}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className='mt-8 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3'>
        <Button.SecondaryLG
          text={t('settings.user_management.delete_alert.cancel')}
          onClick={onClose}
          disabled={isDeleting}
        />
        <Button.WarningLG
          text={t('settings.user_management.delete_alert.delete')}
          onClick={handleDelete}
          isLoading={isDeleting}
        />
      </div>
    </Modal>
  )
}

ModalDeleteUser.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired
}

export default ModalDeleteUser
